// src/admin/pages/AdminBanners.js
import React, { useEffect, useState, useContext } from "react";
import AdminSidebar from "../AdminSidebar";
import AdminHeader from "../AdminHeader";
import { FaSave, FaToggleOn, FaToggleOff } from "react-icons/fa";
import { AdminLanguageContext } from "../context/AdminLanguageContext";
import {
  getAdminSaleBanner,
  saveAdminSaleBanner,
  toggleAdminSaleBanner,
} from "../../services/adminSaleBanner.api";

function AdminSaleBanner() {
  const [isOpen, setIsOpen] = useState(false);
  const { lang } = useContext(AdminLanguageContext);
  
  const [showConfirmSave, setShowConfirmSave] = useState(false);

  const TEXT = {
    EN: {
      title: "Sale Banner",
      active: "Visible",
      inactive: "Hidden",
      toggle: "Toggle Visibility",
      save: "Save",
      saving: "Saving...",
      loading: "Loading...",
      titleEn: "Title (EN)",
      titleAr: "Title (AR)",
      descEn: "Description (EN)",
      descAr: "Description (AR)",
      discount: "Discount (%)",
      confirmSave: "Save changes to Sale Banner?",
      saved: "✅ Saved successfully",
      toggled: "✅ Visibility updated",
    },
    AR: {
      title: "بانر التخفيضات",
      active: "ظاهر",
      inactive: "مخفي",
      toggle: "تغيير الظهور",
      save: "حفظ",
      saving: "جاري الحفظ...",
      loading: "جاري التحميل...",
      titleEn: "العنوان (EN)",
      titleAr: "العنوان (AR)",
      descEn: "الوصف (EN)",
      descAr: "الوصف (AR)",
      discount: "نسبة الخصم (%)",
      confirmSave: "هل تريد حفظ التعديلات؟",
      saved: "✅ تم الحفظ بنجاح",
      toggled: "✅ تم تحديث الظهور",
    },
  }[lang];

  const adminUser = JSON.parse(localStorage.getItem("adminUser") || "{}");
const perms = JSON.parse(localStorage.getItem("staffPermissions") || {});
const isAdmin = adminUser.role === "admin";
const canAccess = isAdmin || perms.can_manage_banners;


  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [toast, setToast] = useState("");
  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2000);
  };

  const [form, setForm] = useState({
    title_en: "",
    title_ar: "",
    description_en: "",
    description_ar: "",
    discount: 40,
    is_active: 1,
  });

  const load = () => {
    setLoading(true);
    getAdminSaleBanner()
      .then((row) => {
        if (!row) {
          // if DB has no row yet, keep defaults (admin can Save to create)
          setForm((p) => ({ ...p }));
        } else {
          setForm({
            title_en: row.title_en || "",
            title_ar: row.title_ar || "",
            description_en: row.description_en || "",
            description_ar: row.description_ar || "",
            discount: Number(row.discount || 0),
            is_active: Number(row.is_active || 0),
          });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleSave = async () => {
  setSaving(true);
  try {
    await saveAdminSaleBanner({
      title_en: form.title_en,
      title_ar: form.title_ar,
      description_en: form.description_en,
      description_ar: form.description_ar,
      discount: Number(form.discount) || 0,
    });
    showToast(TEXT.saved);
    load();
  } finally {
    setSaving(false);
    setShowConfirmSave(false);
  }
};


  const handleToggle = async () => {
    setSaving(true);
    try {
      await toggleAdminSaleBanner();
      showToast(TEXT.toggled);
      load();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`flex bg-gray-100 min-h-screen ${isOpen ? "overflow-hidden" : ""}`}>
      <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />

      <div className="flex-1 lg:ml-64 transition-all duration-300">
        <AdminHeader setIsOpen={setIsOpen} />

        <main className="p-4 sm:p-6 animate-fadeSlide max-w-4xl mx-auto">
  {!canAccess && (
    <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
      <p className="text-red-700 font-semibold">
        {lang === "AR"
          ? "ليس لديك صلاحية لإدارة بانر التخفيضات"
          : "You do not have permission to manage the sale banner."}
      </p>
    </div>
  )}

  {canAccess && (
    <>

          {/* Toast */}
          {toast && (
            <div className="fixed top-5 right-5 z-[9999] bg-black text-white px-4 py-2 rounded-xl shadow">
              {toast}
            </div>
          )}

          <div className="flex items-start justify-between gap-3 mb-6">
            <div>
              <h1 className="text-3xl font-bold text-cyan-700">{TEXT.title}</h1>
              <p className="text-sm text-gray-600 mt-1">
                {form.is_active ? TEXT.active : TEXT.inactive}
              </p>
            </div>

            <button
              onClick={handleToggle}
              disabled={saving || loading}
              className={`px-4 py-2 rounded-xl font-semibold flex items-center gap-2 shadow ${
                form.is_active ? "bg-green-600 text-white" : "bg-gray-700 text-white"
              } ${saving || loading ? "opacity-70 cursor-not-allowed" : "hover:opacity-95"}`}
            >
              {form.is_active ? <FaToggleOn /> : <FaToggleOff />}
              {TEXT.toggle}
            </button>
          </div>

          {loading ? (
            <p className="text-gray-600">{TEXT.loading}</p>
          ) : (
            <div className="bg-white shadow-xl rounded-2xl p-6 space-y-5 border border-gray-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="font-semibold text-gray-700">{TEXT.titleEn}</label>
                  <input
                    className="w-full p-3 border rounded-xl mt-1"
                    value={form.title_en}
                    onChange={(e) => setForm((p) => ({ ...p, title_en: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="font-semibold text-gray-700">{TEXT.titleAr}</label>
                  <input
                    className="w-full p-3 border rounded-xl mt-1"
                    value={form.title_ar}
                    onChange={(e) => setForm((p) => ({ ...p, title_ar: e.target.value }))}
                  />
                </div>
              </div>

              <div>
                <label className="font-semibold text-gray-700">{TEXT.descEn}</label>
                <textarea
                  rows={3}
                  className="w-full p-3 border rounded-xl mt-1"
                  value={form.description_en}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, description_en: e.target.value }))
                  }
                />
              </div>

              <div>
                <label className="font-semibold text-gray-700">{TEXT.descAr}</label>
                <textarea
                  rows={3}
                  className="w-full p-3 border rounded-xl mt-1"
                  value={form.description_ar}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, description_ar: e.target.value }))
                  }
                />
              </div>

              <div className="max-w-xs">
                <label className="font-semibold text-gray-700">{TEXT.discount}</label>
                <input
                  type="number"
                  min={0}
                  max={95}
                  className="w-full p-3 border rounded-xl mt-1"
                  value={form.discount}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, discount: e.target.value }))
                  }
                />
              </div>

              <button
                onClick={() => setShowConfirmSave(true)}
                disabled={saving}
                className={`bg-cyan-600 text-white px-6 py-3 rounded-xl font-semibold flex items-center gap-2 hover:bg-cyan-700 shadow ${
                  saving ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                <FaSave /> {saving ? TEXT.saving : TEXT.save}
              </button>
            </div>
          )}
          
          {showConfirmSave && (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
    <div
      className="bg-white rounded-2xl p-6 w-[360px] shadow-2xl"
      dir={lang === "AR" ? "rtl" : "ltr"}
    >
      <h3 className="text-lg font-bold text-gray-800 mb-2">
        {TEXT.confirmSave}
      </h3>

      <p className="text-sm text-gray-600 mb-6">
        {lang === "EN"
          ? "This will update the Sale Banner for all users."
          : "سيتم تطبيق التغييرات على بانر التخفيضات لجميع المستخدمين."}
      </p>

      <div className="flex justify-end gap-3">
        <button
          onClick={() => setShowConfirmSave(false)}
          className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 transition"
        >
          {lang === "EN" ? "Cancel" : "إلغاء"}
        </button>

        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 rounded-lg bg-cyan-600 text-white hover:bg-cyan-700 transition"
        >
          {lang === "EN" ? "Confirm" : "تأكيد"}
        </button>
      </div>
    </div>
  </div>
)}
    </>
  )}

        </main>
      </div>
    </div>
  );
}

export default AdminSaleBanner;

